import { useMutation } from "@apollo/client";
import { useMemo, useRef, useState } from "react";
import type { SortingState } from "@tanstack/react-table";

import EmptyState from "@/components/EmptyState";
import Table, { MobileTableSortControl } from "@/components/Table";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { showSnackbar } from "@/lib/snackbarUtils";

import CareerPlanStatusDialog, { type CareerPlanStatusDialogMode } from "./_CareerPlanStatusDialog";
import { getColumns, planDisplayTitle, type CareerPlanListRow } from "./_CareerPlanListTableColumns";
import type { HubCareerPlan } from "./careerPlanHubSummaries";
import { CAREER_PLAN_STATUS_SNACKBAR, careerPlanStatusForDialogMode } from "./careerPlanStatusActions";
import { DELETE_CAREER_PLAN_MUTATION, UPDATE_CAREER_PLAN_STATUS_MUTATION } from "./mutations";
import { GET_CAREER_PLAN_HUB_QUERY } from "../queries";

export default function CareerPlanListTable({
  plans,
  pgaId,
  loading,
  canManage = false
}: {
  plans: HubCareerPlan[];
  pgaId: string;
  loading: boolean;
  canManage?: boolean;
}) {
  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [sortingState, setSortingState] = useState<SortingState>([
    { id: "status", desc: false },
    { id: "updatedAt", desc: true }
  ]);
  const [statusDialog, setStatusDialog] = useState<{ planId: string; mode: CareerPlanStatusDialogMode } | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [updateCareerPlanStatus] = useMutation(UPDATE_CAREER_PLAN_STATUS_MUTATION);
  const [deleteCareerPlan] = useMutation(DELETE_CAREER_PLAN_MUTATION);
  const tableData = useMemo<CareerPlanListRow[]>(
    () =>
      plans.map((plan) => ({
        ...plan,
        displayTitle: planDisplayTitle(plan.title),
        lastModified: plan.updatedAt ? toFormat(plan.updatedAt) || "—" : "—"
      })),
    [plans]
  );
  const columns = useMemo(
    () =>
      getColumns({
        setMenu,
        canManage,
        onAction: (plan, mode) => {
          if (savingRef.current || saving) return;
          setStatusError(null);
          setStatusDialog({ planId: plan.id, mode });
        }
      }),
    [canManage, saving, setMenu]
  );
  const refetchQueries = [{ query: GET_CAREER_PLAN_HUB_QUERY, variables: { pgaId } }];

  const closeStatusDialog = () => {
    if (saving) return;
    setStatusDialog(null);
    setStatusError(null);
  };

  const handleStatusConfirm = async () => {
    if (!statusDialog || savingRef.current) return;

    savingRef.current = true;
    setSaving(true);
    setStatusError(null);

    try {
      if (statusDialog.mode === "delete") {
        const mutationResult = await deleteCareerPlan({
          variables: { id: statusDialog.planId },
          refetchQueries,
          awaitRefetchQueries: true
        });
        const mutationErrors = mutationResult.errors;
        const payload = mutationResult.data?.deleteCareerPlan;

        if (mutationErrors?.length) {
          setStatusError(mutationErrors[0]?.message || "Unable to delete Career Plan");
          return;
        }
        if (!payload?.success) {
          setStatusError(payload?.message || "Unable to delete Career Plan");
          return;
        }
      } else {
        const mutationResult = await updateCareerPlanStatus({
          variables: {
            id: statusDialog.planId,
            status: careerPlanStatusForDialogMode(statusDialog.mode)
          },
          refetchQueries,
          awaitRefetchQueries: true
        });
        const mutationErrors = mutationResult.errors;
        const payload = mutationResult.data?.updateCareerPlanStatus;

        if (mutationErrors?.length) {
          setStatusError(mutationErrors[0]?.message || "Unable to update Career Plan status");
          return;
        }
        if (!payload?.success) {
          setStatusError(payload?.message || "Unable to update Career Plan status");
          return;
        }
      }

      showSnackbar(CAREER_PLAN_STATUS_SNACKBAR[statusDialog.mode]);
      setStatusDialog(null);
    } catch (statusMutationError) {
      setStatusError(
        statusMutationError instanceof Error
          ? statusMutationError.message
          : statusDialog.mode === "delete"
            ? "Unable to delete Career Plan"
            : "Unable to update Career Plan status"
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <>
      {canManage ? <Menu /> : null}
      {statusDialog ? (
        <CareerPlanStatusDialog
          open
          mode={statusDialog.mode}
          saving={saving}
          error={statusError}
          onClose={closeStatusDialog}
          onConfirm={() => {
            void handleStatusConfirm();
          }}
        />
      ) : null}
      <Table
        data={tableData}
        loading={loading}
        sortingState={sortingState}
        onSortingChange={setSortingState}
        getRowLink={(row) => ({
          to: `/people/${pgaId}/career/career-profile/plans/${row.original.id}`
        })}
        columns={columns}
        renderControls={
          isCompactLayout && (loading || tableData.length > 0)
            ? (_table, { onSortClick } = {}) => <MobileTableSortControl onSortClick={onSortClick} />
            : undefined
        }
        renderNoDataEmptyState={() => (
          <EmptyState
            iconName="rubric"
            title="No Career Plans"
            caption="This person has not been provided a career plan yet."
          />
        )}
        data-testid="career-plan-table"
      />
    </>
  );
}
